import { FC, useMemo } from 'external/react';
import useAdmin from 'context/admin-context/hook';
import { CourseInfo, Subject, Grade } from 'types/course';

import StyledSelect from './StyledSelect';

const ChangeCourseModalDropdowns: FC = () => {

  const { state, changeCourseModal, updateChangeCourseModal } = useAdmin();

  const { purchaseIndex, selected, source } = changeCourseModal ?? {};

  // originalCourse = the course being replaced, set by caller on open, never changes
  // selected.course = tracks the current pick as user browses (starts = originalCourse)
  // selected.subject / selected.grade = cascade dropdown state
  const originalCourse = changeCourseModal?.originalCourse ?? '';
  const subjectId      = selected?.subject ?? '';
  const gradeId        = selected?.grade   ?? '';
  const courseId       = selected?.course  ?? '';

  // ── Data from context ──────────────────────────────────────────────────────

  const allCourses: CourseInfo[]  = state.courses?.items ?? [];
  const subjects: Subject[]       = state.subjects ?? [];
  const grades: Grade[]           = state.grades   ?? [];

  // IDs already taken by this purchase — originalCourse excluded so it stays selectable
  const takenIds = useMemo<Set<string>>(() => {
    const all: string[] = state.client?.purchases?.items?.[purchaseIndex]?.courses?.all ?? [];
    return new Set(all.filter(id => id !== originalCourse));
  }, [state.client, purchaseIndex, originalCourse]);


  const sortedGrades = useMemo(
    () => [...grades].sort((a, b) => a.precedence - b.precedence),
    [grades]
  );

  // When a grade is selected, narrow subjects to only those with courses for that grade
  // When no grade is selected, show all subjects that have any course
  const availableSubjects = useMemo(() => {
    const subjectSet = gradeId
      ? new Set(allCourses.filter(c => c.grade === gradeId).map(c => c.subject))
      : new Set(allCourses.map(c => c.subject));
    return subjects.filter(s => subjectSet.has(s.id));
  }, [gradeId, allCourses, subjects]);

  // When a subject is selected, narrow grades to only those with courses for that subject
  // When no subject is selected, show all grades that have any course
  const availableGrades = useMemo(() => {
    const gradeSet = subjectId
      ? new Set(allCourses.filter(c => c.subject === subjectId).map(c => c.grade))
      : new Set(allCourses.map(c => c.grade));
    return sortedGrades.filter(g => gradeSet.has(g.id));
  }, [subjectId, allCourses, sortedGrades]);

  const availableCourses = useMemo<CourseInfo[]>(() => {
    if (!subjectId || !gradeId) return [];
    return allCourses.filter(c => c.subject === subjectId && c.grade === gradeId);
  }, [subjectId, gradeId, allCourses]);


  // ── State ──────────────────────────────────────────────────────────────────


  const set = (patch: { subject?: string; grade?: string; course?: string }) =>
    updateChangeCourseModal(
      true,
      purchaseIndex,
      originalCourse,
      {
        source,
        subject: patch.subject !== undefined ? patch.subject : subjectId,
        grade:   patch.grade   !== undefined ? patch.grade   : gradeId,
        course:  patch.course  !== undefined ? patch.course  : courseId,
      },
      null
    );

  const handleSubjectChange = (val: string) => set({ subject: val, grade: '', course: '' });
  const handleGradeChange   = (val: string) => set({ grade: val, course: '' });
  const handleCourseChange  = (val: string) => set({ course: val });

  return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              <StyledSelect
                label="Materia"
                value={subjectId}
                onChange={handleSubjectChange}
                placeholder='— Selecciona una materia —'
              >
                {availableSubjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name.es}</option>
                ))}
              </StyledSelect>
             <StyledSelect
                label="Grado"
                value={gradeId}
                onChange={handleGradeChange}
                placeholder='— Selecciona un grado —'
                emptyMessage={
                  subjectId && availableGrades.length === 0
                    ? 'No hay grados disponibles'
                    : undefined
                }
              >
                {availableGrades.map(g => (
                  <option key={g.id} value={g.id}>{g.name.es}</option>
                ))}
              </StyledSelect>
             <StyledSelect
                label="Curso"
                value={courseId}
                onChange={handleCourseChange}
                disabled={!subjectId || !gradeId}
                placeholder='— Selecciona un curso —'
                emptyMessage={
                  subjectId && gradeId && availableCourses.length === 0
                    ? 'No hay cursos disponibles'
                    : undefined
                }
              >
                {availableCourses.map(c => {
                  const isTaken = takenIds.has(c.id);
                  return (
                    <option key={c.id} value={c.id} disabled={isTaken}>
                      {c.name}{isTaken ? ' (ya seleccionado)' : ''}
                    </option>
                  );
                })}
              </StyledSelect>
            </div>
  );
};

export default ChangeCourseModalDropdowns;